import {
    Autocomplete,
    Box,
    Checkbox,
    createFilterOptions,
    FilterOptionsState,
    TextField,
    Typography,
} from '@mui/material';
import { Q } from '@nozbe/watermelondb';
import withObservables from '@nozbe/with-observables';
import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { of } from 'rxjs';
import { INVOICES_FILTER } from 'shared/constants/invoices/filters';
import Database from 'shared/db/services/Database.web';
import { InvoicesFiltersContactOption } from 'shared/types/invoicesFilters';

import { withSyncContext } from '@/hoc';

import { SelectedContactsList } from './components';
import { Props } from './types';

function ContactsFilterSection({
    contacts,
    filtersState,
    onFiltersChange,
}: Props) {
    const { t } = useTranslation();

    const handleRemoveContactSelection = useCallback(
        (contactToRemove: InvoicesFiltersContactOption) => () => {
            const prevFiltersState = [
                ...filtersState[INVOICES_FILTER.contacts],
            ];

            const setContactsFilterValue = onFiltersChange(
                INVOICES_FILTER.contacts,
            );

            const newContacts = prevFiltersState.filter(
                (contact) => contact.id !== contactToRemove.id,
            );

            setContactsFilterValue(newContacts);
        },
        [filtersState, onFiltersChange],
    );

    const contactsOptions = useMemo(() => {
        if (!contacts?.length) {
            return [];
        }

        return contacts.map((contact) => ({
            id: contact.id,
            name: `${contact.firstName}${
                contact.lastName ? ` ${contact.lastName}` : ''
            }`,
        }));
    }, [contacts]);

    const renderOption = useCallback(
        (
            props: React.HTMLAttributes<HTMLLIElement>,
            option: InvoicesFiltersContactOption,
            { selected },
        ) => {
            return (
                <li
                    {...props}
                    key={option.id}
                    data-test-id={`InvoicesPage-FiltersPanel-SelectContacts-${option.name}`}
                >
                    <Checkbox
                        checked={selected}
                        size="small"
                        sx={{ padding: 0, py: 0.5, ml: -0.5, mr: 1 }}
                    />
                    <Typography fontSize={14}>{option.name}</Typography>
                </li>
            );
        },
        [],
    );

    const getOptionLabel = useCallback(
        (option: InvoicesFiltersContactOption) => {
            return option.name;
        },
        [],
    );

    const renderTags = useCallback(() => {
        return null;
    }, []);

    const handleContactsSelectChange = useCallback(
        (_event, optionsValue: InvoicesFiltersContactOption[]) => {
            const setContactsFilterValue = onFiltersChange(
                INVOICES_FILTER.contacts,
            );

            setContactsFilterValue(optionsValue);
        },
        [onFiltersChange],
    );

    const filterOptions = useCallback(
        (
            options: InvoicesFiltersContactOption[],
            state: FilterOptionsState<InvoicesFiltersContactOption>,
        ) => {
            const defaultFiltersOptions =
                createFilterOptions<InvoicesFiltersContactOption>();

            return defaultFiltersOptions(options, state).slice(0, 50);
        },
        [],
    );

    return (
        <Box className="mb-4 mr-6">
            <Box className="mb-4">
                <Autocomplete
                    data-test-id="InvoicesPage-FiltersPanel-SelectContacts"
                    clearOnBlur={false}
                    disableClearable
                    fullWidth
                    multiple
                    onChange={handleContactsSelectChange}
                    options={contactsOptions}
                    disableCloseOnSelect
                    getOptionLabel={getOptionLabel}
                    renderOption={renderOption}
                    style={{ flexWrap: 'nowrap', width: 200 }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('InvoicesPage:filters:contacts')}
                            size="small"
                        />
                    )}
                    renderTags={renderTags}
                    value={filtersState.contacts}
                    filterOptions={filterOptions}
                />
            </Box>
            {filtersState.contacts.length ? (
                <SelectedContactsList
                    onRemoveContactSelection={handleRemoveContactSelection}
                    selectedContacts={filtersState.contacts}
                />
            ) : null}
        </Box>
    );
}

const enhance = withObservables<Props, unknown>(
    ['isInitialSyncInProgress'],
    ({ isInitialSyncInProgress }) => {
        const database = Database.getDatabase();

        return {
            contacts: isInitialSyncInProgress
                ? of([])
                : database.collections
                      .get('contacts')
                      .query(
                          Q.sortBy('first_name', 'asc'),
                          Q.sortBy('last_name', 'asc'),
                          Q.where('hidden', false),
                      ),
        };
    },
);

export default withSyncContext<Props>(enhance(memo(ContactsFilterSection)));
